import React, { useEffect, useState } from "react";
import {
  getPortalDocument,
  getPortalDocuments,
  getPortalItems,
  PORTAL_ITEM_TYPES,
  ROADMAP_STATUSES,
} from "Projects/CC_cubi_clan_project/Utils/PortalService";
import { isExternalPortalUrl, resolvePortalLink } from "./PortalSecurity";
import "./PortalWorkspace.css";

const STATUS_LABELS = Object.freeze(
  ROADMAP_STATUSES.reduce((result, entry) => ({ ...result, [entry.value]: entry.label }), {})
);

function valueOrUnknown(value) {
  return value == null || String(value).trim() === "" ? "No consta" : String(value);
}

function errorMessage(error, resource) {
  if (error?.status === 401) return "La sesión ha caducado. Inicia sesión de nuevo.";
  if (error?.status === 403) return "No tienes permiso para consultar este " + resource + ".";
  if (error?.status === 404) return "El recurso no existe o no está autorizado.";
  return error?.message || "No se pudo cargar el " + resource + ".";
}

function SafePortalLink({ link }) {
  const href = resolvePortalLink(link);
  const label = valueOrUnknown(link?.label || link?.kind || link?.targetId);
  if (!href) {
    return <span className="portal-link portal-link--disabled" title="Enlace no disponible o rechazado">{label}</span>;
  }
  const external = isExternalPortalUrl(href);
  return <a className="portal-link" href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>{label}</a>;
}

function LinkCollection({ label, entries }) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return (
    <div className="portal-card__links">
      <strong>{label}:</strong>
      {entries.map((entry, index) => {
        const link = typeof entry === "string" ? { label: entry } : entry;
        return <SafePortalLink key={(link?.kind || label) + "-" + (link?.targetId || link?.url || index)} link={link} />;
      })}
    </div>
  );
}

export function PortalItemCard({ item }) {
  const pr = typeof item?.pr === "string" ? { status: item.pr } : (item?.pr || {});
  const prLink = { kind: "PR", label: valueOrUnknown(pr.status), url: pr.url };
  const prUrl = resolvePortalLink(prLink);
  return (
    <article id={"portal-item-" + encodeURIComponent(item?.id || "unknown")} className="portal-card" data-testid={"portal-item-" + (item?.id || "unknown")}>
      <header className="portal-card__header">
        <div><span className="portal-card__id">{valueOrUnknown(item?.id)}</span><h3>{valueOrUnknown(item?.title)}</h3></div>
        <span className="portal-card__status">{STATUS_LABELS[item?.status] || valueOrUnknown(item?.status)}</span>
      </header>
      <p className="portal-card__summary">{valueOrUnknown(item?.summary)}</p>
      <dl className="portal-card__facts">
        <div><dt>Tipo</dt><dd>{valueOrUnknown(item?.type)}</dd></div>
        <div><dt>Proyecto</dt><dd>{valueOrUnknown(item?.project)}</dd></div>
        <div><dt>Prioridad</dt><dd>{valueOrUnknown(item?.priority)}</dd></div>
        <div><dt>Autorización</dt><dd>{valueOrUnknown(item?.authorizationStatus)}</dd></div>
        <div><dt>PR</dt><dd>{prUrl ? <SafePortalLink link={prLink} /> : valueOrUnknown(pr.status)}</dd></div>
        <div><dt>Merge</dt><dd>{valueOrUnknown(item?.mergeStatus)}</dd></div>
        <div><dt>Despliegue</dt><dd>{valueOrUnknown(item?.deploymentStatus)}</dd></div>
        <div><dt>Revisión fuente</dt><dd>{valueOrUnknown(item?.sourceRevision)}</dd></div>
        <div><dt>Fecha fuente</dt><dd>{valueOrUnknown(item?.sourceDate)}</dd></div>
      </dl>
      <LinkCollection label="Dependencias" entries={item?.dependencies} />
      <LinkCollection label="Evidencias" entries={item?.evidence} />
      <LinkCollection label="Enlaces" entries={item?.links} />
    </article>
  );
}

function PortalNotice() {
  return <div className="portal-notice" role="note" data-testid="portal-readonly-notice">Vista informativa de solo lectura. Una tarjeta no ejecuta acciones, no concede autorización y no implica PR, merge ni despliegue.</div>;
}

function PortalError({ error, resource }) {
  return error ? <div className="portal-error" role="alert">{errorMessage(error, resource)}</div> : null;
}

export function PortalCatalogView({ type }) {
  const isRoadmap = type === PORTAL_ITEM_TYPES.ROADMAP;
  const empty = { project: "", status: "", search: "" };
  const [filters, setFilters] = useState(empty);
  const [query, setQuery] = useState(empty);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getPortalItems({ type, ...query })
      .then((rows) => { if (active) setItems(rows); })
      .catch((reason) => { if (active) { setItems([]); setError(reason); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, type]);

  const update = (event) => setFilters((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  const submit = (event) => {
    event.preventDefault();
    setQuery({ project: filters.project.trim(), status: filters.status, search: filters.search.trim() });
  };
  const clear = () => { setFilters(empty); setQuery(empty); };

  return (
    <section className="portal-section" aria-labelledby="portal-catalog-title">
      <div className="portal-heading"><div><h1 id="portal-catalog-title">{isRoadmap ? "Roadmap" : "Decisiones"}</h1><p>Resultados filtrados por el backend autorizado; la interfaz no recibe elementos fuera de permiso.</p></div></div>
      <PortalNotice />
      <form className="portal-filters" onSubmit={submit} aria-label="Filtros del portal">
        <label>Buscar<input name="search" value={filters.search} onChange={update} type="search" /></label>
        <label>Proyecto<input name="project" value={filters.project} onChange={update} /></label>
        <label>Estado<select name="status" value={filters.status} onChange={update}><option value="">Todos</option>{ROADMAP_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></label>
        <div className="portal-filters__actions"><button type="submit">Aplicar filtros</button><button type="button" className="portal-button--secondary" onClick={clear}>Limpiar</button></div>
      </form>
      <PortalError error={error} resource={isRoadmap ? "roadmap" : "registro de decisiones"} />
      {loading ? <p role="status">Cargando…</p> : null}
      {!loading && !error && items.length === 0 ? <p className="portal-empty">No hay resultados autorizados.</p> : null}
      <div className="portal-grid">{items.map((item) => <PortalItemCard key={item.id} item={item} />)}</div>
    </section>
  );
}

function DocumentMetadata({ document }) {
  return (
    <dl className="portal-card__facts portal-document__facts">
      <div><dt>ID</dt><dd>{valueOrUnknown(document?.id)}</dd></div>
      <div><dt>Clasificación</dt><dd>{valueOrUnknown(document?.classification)}</dd></div>
      <div><dt>Proyecto</dt><dd>{valueOrUnknown(document?.project)}</dd></div>
      <div><dt>Ruta fuente</dt><dd>{valueOrUnknown(document?.sourcePath)}</dd></div>
      <div><dt>Revisión</dt><dd>{valueOrUnknown(document?.sourceRevision)}</dd></div>
      <div><dt>SHA-256</dt><dd className="portal-document__hash">{valueOrUnknown(document?.sha256)}</dd></div>
      <div><dt>Fecha fuente</dt><dd>{valueOrUnknown(document?.sourceDate)}</dd></div>
      <div><dt>Fecha build</dt><dd>{valueOrUnknown(document?.buildDate)}</dd></div>
      <div><dt>Permiso</dt><dd>{valueOrUnknown(document?.requiredPermission)}</dd></div>
    </dl>
  );
}

export function PortalDocumentView({ initialDocumentId, initialClassification = "", onSelectDocument }) {
  const empty = { project: "", classification: initialClassification, search: "" };
  const [filters, setFilters] = useState(empty);
  const [query, setQuery] = useState(empty);
  const [documents, setDocuments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    setFilters((previous) => ({ ...previous, classification: initialClassification }));
    setQuery((previous) => ({ ...previous, classification: initialClassification }));
  }, [initialClassification]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getPortalDocuments(query)
      .then((rows) => { if (active) setDocuments(rows); })
      .catch((reason) => { if (active) { setDocuments([]); setError(reason); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query]);

  useEffect(() => {
    if (!initialDocumentId) { setSelected(null); return undefined; }
    let active = true;
    setDetailLoading(true);
    setDetailError(null);
    getPortalDocument(initialDocumentId)
      .then((document) => { if (active) setSelected(document); })
      .catch((reason) => { if (active) { setSelected(null); setDetailError(reason); } })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [initialDocumentId]);

  const update = (event) => setFilters((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  const submit = (event) => {
    event.preventDefault();
    setQuery({ ...filters, project: filters.project.trim(), search: filters.search.trim() });
  };

  return (
    <section className="portal-section" aria-labelledby="portal-documents-title">
      <div className="portal-heading"><div><h1 id="portal-documents-title">Documentos</h1><p>Contenido publicado por manifiesto de build y servido exclusivamente por la API autorizada.</p></div></div>
      <form className="portal-filters" onSubmit={submit} aria-label="Filtros de documentos">
        <label>Buscar<input name="search" value={filters.search} onChange={update} type="search" /></label>
        <label>Proyecto<input name="project" value={filters.project} onChange={update} /></label>
        <label>Clase<select name="classification" value={filters.classification} onChange={update}><option value="">Todas las autorizadas</option><option value="INTERNAL">Interna</option><option value="OPS_INTERNAL">Operaciones interna</option></select></label>
        <div className="portal-filters__actions"><button type="submit">Aplicar filtros</button></div>
      </form>
      <PortalError error={error} resource="catálogo documental" />
      {loading ? <p role="status">Cargando…</p> : null}
      {!loading && !error && documents.length === 0 ? <p className="portal-empty">No hay documentos autorizados.</p> : null}
      <div className="portal-document-layout">
        <div className="portal-document-list">
          {documents.map((document) => <article className="portal-document-summary" key={document.id}><h2>{valueOrUnknown(document.title)}</h2><DocumentMetadata document={document} /><button type="button" onClick={() => onSelectDocument?.(document.id)}>Ver documento</button></article>)}
        </div>
        <div className="portal-document-detail" aria-live="polite">
          <PortalError error={detailError} resource="documento" />
          {detailLoading ? <p role="status">Cargando documento…</p> : null}
          {!detailLoading && selected ? <article><h2>{valueOrUnknown(selected.title)}</h2><DocumentMetadata document={selected} /><pre data-testid="portal-document-content">{valueOrUnknown(selected.content)}</pre></article> : null}
          {!detailLoading && !selected && !detailError ? <p className="portal-empty">Selecciona un documento autorizado.</p> : null}
        </div>
      </div>
    </section>
  );
}
